import { useBrand } from './useBrand'
import { useAccount } from './useAccount'

/**
 * "Has this brand booked their onboarding call?" — the single read used by the
 * Home Get-Started checklist, the Listings call banner, and the once-ever
 * post-publish interstitial (free-platform pivot, Aug 2026).
 *
 * ONE source of truth: brand_configs.launch_call_booked_at, which only ever
 * comes from Calendly.
 *
 *   - The org webhook (worker/src/calendly_webhook.ts) stamps it on
 *     invitee.created for every booking it can resolve to a brand, and clears
 *     it on invitee.canceled once no live booking remains.
 *   - Signup books before the brand row exists, so there is nothing to stamp
 *     at the time. /api/onboard reads the webhook's booked_calls row back by
 *     invitee email and stamps from that — still Calendly's data, resolved
 *     from the other direction.
 *
 * There is deliberately NO local "they just booked" flag. An earlier version
 * had one to cover the seconds before the webhook lands, and it was a bad
 * trade: localStorage can only be set, never corrected. It couldn't know the
 * booking resolved to a different brand, or that the call was later cancelled,
 * so it outlived the truth and started asserting bookings that hadn't happened
 * — including for brands who had explicitly skipped. A checklist that lies
 * about a booking is worse than one that takes a few seconds to catch up.
 *
 * Catching up is handled where a booking can actually be observed, by
 * refetching the brand rather than guessing at it: LaunchCall polls after its
 * embed reports a booking, and BrandFocusRefresh (App.tsx) refreshes when the
 * tab regains focus, which covers the links that open Calendly in a new tab.
 */

/** Listings call banner dismissal. A per-viewer UI preference, not a fact about the brand. */
export const BANNER_DISMISSED_KEY = 'bmc_listings_call_banner_dismissed'
/** The post-publish interstitial is show-once-ever per browser (Mike, 2026-08-24). */
export const INTERSTITIAL_SEEN_KEY = 'bmc_launch_call_interstitial_seen'

export function readFlag(key: string): boolean {
  try { return localStorage.getItem(key) === '1' } catch { return false }
}

export function writeFlag(key: string, value: boolean): void {
  try { localStorage.setItem(key, value ? '1' : '0') } catch { /* private mode / quota */ }
}

/** Authoritative read of the brand's onboarding-call state. */
export function useLaunchCallBooked(): boolean {
  const { brand } = useBrand()
  return !!brand?.launch_call_booked_at
}

/**
 * Should this brand be shown ANY "book your onboarding call" prompt?
 *
 * False for every brand under an agency. The agency books once for its whole
 * roster (accounts.launch_call_booked_at, 20260901050000) and works that
 * checklist from its own lobby — so nobody sitting inside one of its brands
 * should be asked to book a call that isn't theirs to book, and a five-brand
 * agency should not be nagged five times.
 *
 * Covers all three surfaces together, on purpose: the Home Get-Started
 * checklist, the Listings call banner, and the once-ever post-publish
 * interstitial. Suppressing only one of them is what makes the other two look
 * like bugs.
 *
 * Note this is NOT the same as pretending the brand has booked. The brand's own
 * launch_call_booked_at stays whatever it is — this hook hides the ASK, and
 * anything that reads the booking itself keeps reading the truth.
 */
export function useCallPromptsSuppressed(): boolean {
  const { account } = useAccount()
  return account?.type === 'agency'
}
